/**
 * Reactive view of undelivered outbox work for list filtering.
 *
 * A message sitting in the local outbox is work the server cannot see:
 * its chat has no queued row, so server-derived fields (`queue_depth`,
 * `active_work_depth`, topic `status`) all read idle and the chat would
 * sort under the "Done" filter while Mark is looking at an undelivered
 * bubble. These sets let the chat-list and topic-list filters count
 * pending outbox work as live. The upload-progress hook lets the pending
 * bubble itself show a long attachment upload as moving, not as failed.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'
import {
  listOutbox,
  outboxUploadProgress,
  subscribeOutbox,
  subscribeOutboxUploadProgress,
  type OutboxUploadProgress,
} from './outbox'

export type OutboxPendingScopes = {
  /** Chat ids with at least one undelivered outbox item. */
  chatIds: ReadonlySet<string>
  /** `${chatId}::${topicId}` keys with at least one undelivered item. */
  topicKeys: ReadonlySet<string>
}

const EMPTY_SCOPES: OutboxPendingScopes = {
  chatIds: new Set<string>(),
  topicKeys: new Set<string>(),
}

export function outboxTopicKey(chatId: string, topicId: string): string {
  return `${chatId}::${topicId}`
}

const OUTBOX_PENDING_QUERY_KEY = ['outbox-pending-scopes'] as const

export function useOutboxPendingScopes(): OutboxPendingScopes {
  const qc = useQueryClient()
  const { data } = useQuery<OutboxPendingScopes>({
    queryKey: OUTBOX_PENDING_QUERY_KEY,
    queryFn: async () => {
      const items = await listOutbox()
      const chatIds = new Set<string>()
      const topicKeys = new Set<string>()
      for (const item of items) {
        chatIds.add(item.chat_id)
        if (item.topic_id) topicKeys.add(outboxTopicKey(item.chat_id, item.topic_id))
      }
      return { chatIds, topicKeys }
    },
    staleTime: Infinity,
    networkMode: 'always',
  })

  useEffect(() => subscribeOutbox(() => {
    void qc.invalidateQueries({ queryKey: OUTBOX_PENDING_QUERY_KEY })
  }), [qc])

  return data ?? EMPTY_SCOPES
}

/** Live upload progress for one undelivered outbox message, or null when
 * none of its attachments is uploading from this page right now. */
export function useOutboxUploadProgress(outboxId: string | null): OutboxUploadProgress | null {
  return useSyncExternalStore(
    subscribeOutboxUploadProgress,
    () => (outboxId ? outboxUploadProgress(outboxId) : null),
    () => null,
  )
}
